import { useAppStore } from '../stores/useAppStore';
import type { Chat, Settings, Slug, Story, Universe } from '../types';
import {
  deleteBlob,
  deleteObject,
  isSyncConfigured,
  putBlob,
  putObject,
} from './syncClient';

/**
 * Per-id debounce window. Streaming writes (canon updates, chat-message
 * appends during a turn) fire many times per second; we coalesce them into
 * one PUT per id per window.
 */
const DEBOUNCE_MS = 1500;

type PendingWrite =
  | { type: 'putObject'; kind: string; id: string; value: unknown }
  | { type: 'deleteObject'; kind: string; id: string }
  | {
      type: 'putBlob';
      key: string;
      body: Blob | string;
      mime?: string;
    }
  | { type: 'deleteBlob'; key: string };

type PendingEntry = {
  timeout: ReturnType<typeof setTimeout>;
  write: PendingWrite;
};

const pending = new Map<string, PendingEntry>();

let suspended = false;

function keyFor(w: PendingWrite): string {
  switch (w.type) {
    case 'putObject':
    case 'deleteObject':
      return `obj:${w.kind}:${w.id}`;
    case 'putBlob':
    case 'deleteBlob':
      return `blob:${w.key}`;
  }
}

function execute(write: PendingWrite): void {
  if (!isSyncConfigured()) return;
  let p: Promise<unknown>;
  switch (write.type) {
    case 'putObject':
      p = putObject(write.kind, write.id, write.value);
      break;
    case 'deleteObject':
      p = deleteObject(write.kind, write.id);
      break;
    case 'putBlob':
      p = putBlob(write.key, write.body, write.mime);
      break;
    case 'deleteBlob':
      p = deleteBlob(write.key);
      break;
  }
  p.catch((e) => {
    console.warn(`[push] ${write.type} ${keyFor(write)} failed`, e);
  });
}

function schedule(write: PendingWrite): void {
  if (suspended) return;
  const k = keyFor(write);
  const existing = pending.get(k);
  if (existing) clearTimeout(existing.timeout);
  const timeout = setTimeout(() => {
    pending.delete(k);
    execute(write);
  }, DEBOUNCE_MS);
  pending.set(k, { timeout, write });
}

export function schedulePutObject(kind: string, id: string, value: unknown): void {
  schedule({ type: 'putObject', kind, id, value });
}

export function scheduleDeleteObject(kind: string, id: string): void {
  schedule({ type: 'deleteObject', kind, id });
}

export function schedulePutBlob(
  key: string,
  body: Blob | string,
  mime?: string
): void {
  schedule({ type: 'putBlob', key, body, mime });
}

export function scheduleDeleteBlob(key: string): void {
  schedule({ type: 'deleteBlob', key });
}

/**
 * Fire every pending write immediately. Called on tab-hide and before-unload
 * so we don't leak unflushed edits.
 */
export function flushPushSync(): void {
  for (const { timeout, write } of pending.values()) {
    clearTimeout(timeout);
    execute(write);
  }
  pending.clear();
}

type Snapshot = {
  settings: Settings;
  universes: Record<string, Universe>;
  slugPool: Record<string, Slug>;
  stories: Record<string, Story>;
  chats: Record<string, Chat>;
};

function snapshot(): Snapshot {
  const s = useAppStore.getState();
  const slugPool: Record<string, Slug> = {};
  for (const slug of s.slugPool) slugPool[slug.id] = slug;
  return {
    settings: s.settings,
    universes: s.universes,
    slugPool,
    stories: s.stories,
    chats: s.chats,
  };
}

function stripSecrets(s: Settings): Settings {
  return { ...s, apiBaseUrl: undefined, apiToken: undefined };
}

function diffMap<T>(
  kind: string,
  prev: Record<string, T>,
  cur: Record<string, T>
): void {
  for (const id of Object.keys(cur)) {
    if (prev[id] !== cur[id]) {
      schedulePutObject(kind, id, cur[id]);
    }
  }
  for (const id of Object.keys(prev)) {
    if (!(id in cur)) {
      scheduleDeleteObject(kind, id);
    }
  }
}

let prev: Snapshot | null = null;

function onStateChange(): void {
  if (suspended) {
    // Re-snapshot will happen at resume; nothing to do here.
    return;
  }
  if (!prev) {
    prev = snapshot();
    return;
  }
  const cur = snapshot();

  if (prev.settings !== cur.settings) {
    const a = JSON.stringify(stripSecrets(prev.settings));
    const b = JSON.stringify(stripSecrets(cur.settings));
    if (a !== b) {
      schedulePutObject('settings', 'global', stripSecrets(cur.settings));
    }
  }
  diffMap('universe', prev.universes, cur.universes);
  diffMap('slug', prev.slugPool, cur.slugPool);
  diffMap('story', prev.stories, cur.stories);
  diffMap('chat', prev.chats, cur.chats);

  prev = cur;
}

let started = false;

export function startPushSync(): void {
  if (started) return;
  started = true;
  prev = snapshot();
  useAppStore.subscribe(() => onStateChange());

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushPushSync();
    });
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => flushPushSync());
    window.addEventListener('beforeunload', () => flushPushSync());
  }
}

/**
 * Used by `bootstrapFromServer` to silence echoes while it overwrites local
 * state with what we just pulled from the server.
 */
export function suspendPushSync(): void {
  suspended = true;
}

export function resumePushSync(): void {
  suspended = false;
  prev = snapshot();
}
